import generateQueryParams from '@/utils/generateQueryParams';
import axiosInstance from '@/plugins/api/axiosInstance';

export default function shipment() {
    return {
        index,
        get,
        remove,
        voidShipment,
        save,
        addPackage,
        updatePackage,
        removePackage,
        sendEmail,
        close,
        reopen,
        deleteStatus,
        addImage,
        removeImage,
        addDocument,
        removeDocument,
        addService,
        removeService,
        printLabel,
        printCustomLabel,
        updateService,
        repackPackage,
        createShipmentWithReceipt,
        exportGuideFile,
        getBodyEmail
    };

    function save(payload) {
        if (payload.id) {
            return axiosInstance.patch('guide/update', payload);
        } else {
            return axiosInstance.post('guide/create', payload);
        }
    }

    function index(options = {}) {
        const defaultValues = {
            pattern: '',
            limit: 25,
            page: 1
        };
        const params = generateQueryParams(options, defaultValues);
        return axiosInstance.get(`guide/search${params}`);
    }

    function exportGuideFile(options = {}, type = 'pdf') {
        const defaultValues = {
            pattern: '',
            limit: 25,
            page: 1,
            format: type
        };

        let params = {};
        let headers = {};

        if (type === 'pdf') {
            const args = options ? JSON.parse(options) : null;
            params = generateQueryParams(args.query, defaultValues);
            headers = {
                timeout: 60000,
                responseType: 'arraybuffer',
                headers: {
                    Accept: 'application/pdf',
                    'Content-Type': 'application/json'
                }
            };
        } else {
            params = generateQueryParams(options, defaultValues);
            headers = {
                timeout: 60000,
                responseType: 'arraybuffer'
            };
        }
        return axiosInstance.get(`guide/export${params}`, headers);
    }

    function get(guideId) {
        return axiosInstance.get(`guide/get?id=${guideId}`);
    }

    function remove(guideId, packid) {
        return axiosInstance.post(`guide/remove`, {
            guideid: guideId,
            packid: packid
        });
    }

    function voidShipment(id) {
        return axiosInstance.delete('guide/cancel', {
            data: {
                id
            }
        });
    }

    function addPackage(shipmentId, payload) {
        const formData = new FormData();
        formData.append('guideId', shipmentId);

        if (Array.isArray(payload.files)) {
            payload.files.forEach((file) => {
                formData.append('files[]', file);
            });
        }

        Object.entries(payload).forEach(([key, value]) => {
            formData.append(key, value);
        });

        return axiosInstance.post('guide/add-receipts', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    function updatePackage(guideId, payload) {
        return axiosInstance.patch('guide/update-receipts', {
            guideId,
            ...payload
        });
    }

    function removePackage(guideId, packageId) {
        return axiosInstance.delete('guide/remove-receipt', {
            data: {
                guideId,
                receiptIds: [packageId]
            }
        });
    }

    function deleteStatus(statusId) {
        return axiosInstance.delete(`guide/delete-status`, {
            data: statusId
        });
    }

    function close(options) {
        const formData = new FormData();
        formData.append('id', options.id);
        formData.append('comment', options['comment']);
        formData.append('sendNotification', options['sendNotification']);

        if (options['signature']) {
            formData.append(
                'signature',
                options['signature'],
                `signature_${options.id}.png`
            );
        }

        if (options['images']) {
            options.images.forEach((image) => {
                formData.append('images[]', image.file);
            });
        }

        return axiosInstance.post('guide/delivered', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    function reopen(data) {
        return axiosInstance.post(`guide/reopen`, data);
    }

    function addImage(options, type = 'GALLERY') {
        const formData = new FormData();
        formData.append('id', options.id);
        formData.append('type', type);
        formData.append('files[]', options.picture);

        return axiosInstance.post('guide/add-image', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    function removeImage(imageId, guideId) {
        return axiosInstance.delete(`guide/delete-image`, {
            data: {
                guideId: guideId,
                imageId: imageId
            }
        });
    }

    function addDocument(guideId, document) {
        const formData = new FormData();
        formData.append('documentId', guideId);
        formData.append('file', document);
        return axiosInstance.post('guide/add-document', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    function removeDocument(docId, guideId) {
        return axiosInstance.delete('guide/delete-document', {
            data: {
                guideId: guideId,
                documentId: docId
            }
        });
    }

    function sendEmail({ id, emails, body, subject }) {
        return axiosInstance.post(`guide/send-emails`, {
            id: id,
            emails: emails,
            body: body,
            subject: subject
        });
    }

    function addService(payload) {
        return axiosInstance.post('guide/add-services', payload);
    }

    function removeService(payload) {
        return axiosInstance.delete('guide/remove-services', {
            data: payload
        });
    }

    function printLabel(id) {
        return axiosInstance.get(`guide/label?id=${id}`, {
            timeout: 60000,
            responseType: 'arraybuffer',
            headers: {
                Accept: 'application/pdf',
                'Content-Type': 'application/json'
            }
        });
    }

    function printCustomLabel(id, formatId) {
        return axiosInstance.get(
            `guide/label-custom?id=${id}&labelFormatId=${formatId}`,
            {
                timeout: 60000,
                responseType: 'arraybuffer',
                headers: {
                    Accept: 'application/pdf',
                    'Content-Type': 'application/json'
                }
            }
        );
    }

    function updateService(payload) {
        return axiosInstance.post('guide/update-services', payload);
    }

    function repackPackage(payload) {
        const formData = new FormData();

        formData.append('shipper', payload.shipper);
        formData.append('receiver', payload.receiver);
        formData.append('agency', payload.agency?.id ?? payload.agency);
        formData.append('agent', payload.agent?.id ? payload.agent : null);
        formData.append('location', payload.location?.id ?? payload.location);
        formData.append('shippingType', payload.shippingType ?? 'Ninguna');
        formData.append('aux', payload.aux ?? '');
        formData.append('note', payload.note ?? '');
        formData.append('sendEmailToShipper', payload.sendEmailToShipper);
        formData.append('sendEmailToConsignee', payload.sendEmailToConsignee);

        if (payload.repackingRequestId != null) {
            formData.append('repackingRequestId', payload.repackingRequestId);
        }

        if (
            Array.isArray(payload.listPackage) &&
            payload.listPackage.length > 0
        ) {
            payload.listPackage.forEach((id) => {
                formData.append('idsToReempack[]', id);
            });
        }

        payload.receiptsInShipmentList.forEach((pkg) => {
            const format = {
                arriveDate: payload.arriveDate,
                tracking: pkg.tracking,
                description: pkg.description,
                value: pkg.value,
                weight: pkg.weight,
                length: pkg.length,
                width: pkg.width,
                height: pkg.height,
                pieces: parseInt(pkg.numberPack, 10),
                volume: pkg.volume
            };

            formData.append('receiptsInGuide[]', JSON.stringify(format));
        });

        return axiosInstance.post('guide/reempack-receipts', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    function createShipmentWithReceipt(data) {
        return axiosInstance.post(`guide/create-with-receipts`, data);
    }

    function getBodyEmail(id) {
        return axiosInstance.get(`guide/get-body-email?id=${id}`);
    }
}
